import React, { useEffect, useState } from "react";
import SearchInput from "../../generic/elements/search/SearchInput";
import SelectCarePlanStatus from "./SelectCarePlanStatus";
import CarePlan from "./CarePlan";
import { useGetRole } from "../../generic/hooks/useGetRole";
import { RoleToLinkMapper } from "../../utils/UrlUtils";
import { useNavigate } from "react-router";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { Col, Row } from "antd";
import ModalFormsListComponent from "../../careprovider/components/forms/ModalFormsListComponent";
import { getApiUrl } from "../../../service/network/UrlMapper";
import { performPostRequest } from "../../../service/network/NetworkService";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  careplanStatusRecoil,
  defaultFiltersAtom,
  searchKeywordAtom,
} from "../../../components/recoil/careplanStatusRecoil";
import { carePlanTableDataState } from "../../../components/recoil/carePlanDataRecoil";
import { exportCarePlansToExcel } from "./exportCarePlanExcel";
import { notification } from "antd";
import LegendWrapper from "./legend/LegendWrapper";
import ConfigurationConstants from "../../utils/configurationconstants";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";

const CarePlanWrapper = ({ workforceIdentifier }) => {
  const role = useGetRole();
  const organizationIdentifier = useGetOrgIdentifier();
  const navigate = useNavigate();
  const configuration = useRecoilValue(currentConfiguration);
  const [carePlanStatus, setCarePlanStatus] =
    useRecoilState(careplanStatusRecoil);
  const [searchKeyword, setSearchKeyword] = useRecoilState(searchKeywordAtom);
  const [defaultFilters, setDefaultFilters] =
    useRecoilState(defaultFiltersAtom);
  const [showFormsList, setShowFormsList] = useState(false);
  const [allowExport, setAllowExport] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showForms, setShowForms] = useState(false);
  const carePlanTableData = useRecoilValue(carePlanTableDataState);
  const formStatuses = ["Publish"];

  const handleCreateVolunteerRequest = () => {
    navigate(
      `${RoleToLinkMapper(
        role,
      )}request-for-volunteering-service?showCancel=${true}`,
    );
    window.scrollTo(0, 0);
  };

  const handleExport = async () => {
    if (!carePlanTableData?.length) {
      notification.warning({
        message: "No care plans to export",
        placement: "bottomRight",
      });
      return;
    }
    setExportLoading(true);
    try {
      await exportCarePlansToExcel(carePlanTableData);
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    const getDefaultStatus = async () => {
      const payload = {
        intent: "get_configuration",
        payload: {
          configurationKey: ["niramaya.careplan.default.status"],
        },
        context: {
          organizationIdentifier,
        },
      };
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(payload),
        );
        const response = await result.json();

        const parsedStatus = JSON.parse(response[0].configurationValue || "{}");
        setCarePlanStatus(parsedStatus?.[role] || []);
        if (parsedStatus?.[role]) {
          setDefaultFilters(true);
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (!defaultFilters) {
      getDefaultStatus();
    }
  }, [
    defaultFilters,
    organizationIdentifier,
    role,
    setCarePlanStatus,
    setDefaultFilters,
  ]);

  useEffect(() => {
    const configurationAllowApproveInvoice =
      configuration[ConfigurationConstants.FEATUREACCESS_CAREPLAN_EXPORT];
    setAllowExport(configurationAllowApproveInvoice?.visibility);

    const configurationVisitsTabs =
      configuration[
        ConfigurationConstants.FEATUREACCESS_HOMEPAGE_VOLUNTEER_FORMS
      ];
    setShowForms(configurationVisitsTabs?.visibility);
  }, [configuration]);

  return (
    <>
      <div className="careplan-wrapper row">
        <div className="col-12 col-md-6 careplan-search-wrapper">
          <SearchInput
            searchQuery={searchKeyword}
            setSearchQuery={setSearchKeyword}
            cssClass="searchFilterContainer"
            searchPlaceholder="Search by name, mrn or service"
          />
        </div>

        {role === "Volunteer Coordinator" && (
          <>
            <div className="col-12 col-md-6">
              <SelectCarePlanStatus />
            </div>

            <Row style={{ gap: 4 }} justify="end">
              {allowExport && (
                <Col>
                  <PrimaryButton
                    buttonText="Utilization"
                    handleOnClick={handleExport}
                    loading={exportLoading}
                  />
                </Col>
              )}
              {showForms && (
                <Col>
                  <PrimaryButton
                    buttonText="Forms"
                    handleOnClick={() => setShowFormsList(!showFormsList)}
                  />
                </Col>
              )}
              <Col>
                <PrimaryButton
                  buttonText="Request Volunteer"
                  handleOnClick={handleCreateVolunteerRequest}
                />
              </Col>
            </Row>
          </>
        )}
      </div>

      <LegendWrapper />

      {defaultFilters && (
        <CarePlan
          workforceIdentifier={workforceIdentifier}
          carePlanStatus={carePlanStatus}
        />
      )}

      {showFormsList && (
        <>
          <ModalFormsListComponent
            isModalOpen={showFormsList}
            closeModal={setShowFormsList}
            onFooterButtonClicked={() => setShowFormsList(false)}
            formStatuses={formStatuses}
          />
        </>
      )}
    </>
  );
};

export default CarePlanWrapper;
